import React from "react";
import { Breadcrumbs as MUIBreadCrumbs, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import styled from "styled-components";
import { PathnameMap } from "../../interfaces/breadcrumbs";

export interface IBreadCrumbsProps {
  pathnameMap: PathnameMap;
  pathname: string;
}

const LinkRouter = (props) => <Link {...props} component={RouterLink} />;

const StyledLinkRouter = styled(LinkRouter)(({ theme, ...p }) => ({
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.fontSize,
  ...(p.$inactiveStyles && {
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  }),
  ...(p.$activeStyles && {
    color: theme.palette.neutral.dark,
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  }),
  ...(p.$staticStyles && {
    color: theme.palette.neutral.dark,
    "&:hover, &:focus": {
      textDecoration: "none",
    },
  }),
}));

export const Breadcrumbs = ({
  pathnameMap,
  pathname,
}: IBreadCrumbsProps): JSX.Element | null => {
  const pathnames = pathname.split("/").filter((x) => x !== "");
  let first = true;
  const elements: JSX.Element[] = [];

  const generateElements = (index = 0) => {
    if (index === pathnames.length) return null;

    generateElements(index + 1);

    const to = `/${pathnames.slice(0, index + 1).join("/")}`;
    const matchedPath = first && pathname === to;

    if (!pathnameMap[to]) {
      logger.warn(`Cannot find path ${to} in map. Returning NULL`);
      return null;
    }
    const props = {
      key: to,
      "data-id": `crumb-${pathnames[index]}`,
      to,
      color: "inherit",
    };

    const label = pathnameMap[to];

    if (matchedPath || first) first = false;
    logger.debug(`Inserting breadcrumb for path ${to}.`);
    elements.unshift(
      <StyledLinkRouter
        $staticStyles={matchedPath}
        $activeStyles={!matchedPath && first}
        $inactiveStyles={!matchedPath && !first}
        {...props}
      >
        {label}
      </StyledLinkRouter>
    );
    return undefined;
  };

  generateElements();

  return (
    <MUIBreadCrumbs aria-label="breadcrumb" data-id="breadcrumb" separator="/">
      {elements}
    </MUIBreadCrumbs>
  );
};
